import { useMemo, useState } from "react";
import { featuredComics } from "@/mocks/comics";

const typeFilter = ["全部", "国漫动画", "经典美术片", "木偶动画", "漫画原作"];

const statusStyle: Record<string, string> = {
  已补档: "bg-primary-100 text-primary-800 border-primary-200",
  补档中: "bg-secondary-100 text-secondary-800 border-secondary-200",
  绝版求补: "bg-accent-100 text-accent-800 border-accent-200",
};

const coverStyle: Record<string, string> = {
  "国漫动画": "from-accent-900/40",
  "经典美术片": "from-primary-900/40",
  "木偶动画": "from-secondary-800/40",
  "漫画原作": "from-foreground-900/40",
};

export default function Featured() {
  const [type, setType] = useState("全部");
  const [status, setStatus] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    return featuredComics.filter((c) => {
      const matchType = type === "全部" || c.type === type;
      const matchStatus = status === "全部" || c.status === status;
      const kw = keyword.trim().toLowerCase();
      const matchKw =
        !kw || c.title.includes(keyword.trim()) || c.titleEn.toLowerCase().includes(kw) || c.desc.includes(keyword.trim());
      return matchType && matchStatus && matchKw;
    });
  }, [type, status, keyword]);

  return (
    <section id="classics" className="scroll-mt-20 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xl">
            <p className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-widest text-accent-700">
              <span className="h-px w-6 bg-accent-500"></span>
              经典老番补档列表
            </p>
            <h2 className="text-2xl md:text-4xl">那些年我们追过的经典</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              国漫动画、美术片、木偶剧到漫画原作，逐部补齐片源与幕后资料，标注清楚补档状态。
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索你记忆里的那部…"
                aria-label="搜索经典老番"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50 sm:w-64"
              />
            </div>
            <a
              href="#contribute"
              className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-add-line"></i>
              提交补档请求
            </a>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500">题材</span>
          {typeFilter.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                type === t
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
              }`}
            >
              {t}
            </button>
          ))}
          <span className="ml-2 hidden text-xs text-foreground-500 sm:inline">状态</span>
          {["全部", "已补档", "补档中", "绝版求补"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                status === s
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-800"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 py-16 text-center">
            <i className="ri-file-search-line text-4xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-600">
              没找到「{keyword}」相关的补档，换个名字试试，或提交补档请求让我们考古。
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((c) => (
              <article
                key={c.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="cover-fade relative aspect-[3/4] overflow-hidden">
                  <img
                    src={c.cover}
                    alt={`${c.title} 老漫画补档封面`}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute right-2 top-2 rounded-full border bg-background-950/40 px-2 py-0.5 text-xs text-background-50 backdrop-blur-sm">
                    {c.era}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-base leading-snug md:text-lg">{c.title}</h3>
                    <span
                      className={`shrink-0 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] ${statusStyle[c.status]}`}
                    >
                      {c.status}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-foreground-500">
                    {c.titleEn} · {c.year}
                  </p>
                  <p className="mt-2 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-600 md:text-sm">
                    {c.desc}
                  </p>
                  <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
                    <span className="text-xs text-foreground-500">{c.episodes}</span>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-700 transition-colors hover:text-primary-800"
                    >
                      <i className="ri-vidicon-line"></i>
                      查看补档
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}